import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {
  IE3IsStiDownResponse,
  IE3IsStiDownType,
  IE3StiDownType,
  PortOfCall,
  Role,
  StormPilotage,
  UserProfile
} from '@portbase/bezoekschip-service-typescriptmodels';
import * as Sentry from '@sentry/angular';
import lodash, {isString} from "lodash";
import moment from "moment/moment";
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import newCommercialReleaseScreenSettings from "./commercial-release/new-commercial-release-screen-settings.json";
import {Alert, AlertLevel} from './common/status-alert/alert';
import {RouteData} from "./route-data";
import newCargoScreenSettings from "./visit-details/new-cargo-screen-settings.json";
import {VisitContext} from './visit-details/visit-context';

export class AppContext {

  static userProfile: UserProfile;
  static alerts: Alert[] = [];
  static stormPilotageRotterdam: StormPilotage;
  static stormPilotageAmsterdam: StormPilotage;
  // Deliberately NOT part of `alerts`/`Alert[]`: `registerError`'s string-branch hardcodes
  // `type: 'visit'`, and `clearVisitAlerts()` (called from sendCommand/sendCommandAndWait/
  // checkValidity, i.e. on essentially every visit-flow command) wipes every alert tagged
  // 'visit'. A global STI-down banner living in that array would get silently wiped the
  // moment any unrelated visit command fires. Kept as its own static field instead, polled
  // from AppComponent and read directly by app.component.html.
  static stiDownStatus: IE3IsStiDownResponse | null = null;
  static pendingProcesses: any[] = [];
  static environment: string = AppContext.detectEnvironment();
  static credentialsEnvironment: string = AppContext.detectCredentialsEnvironment();
  static iamEnvironment: string = AppContext.detectIamEnvironment();
  static urlPrefix: string = AppContext.detectUrlPrefix();
  static version: string;
  static wasteOnly: boolean;
  static supportPage = 'https://support.portbase.com';
  static supportPageForTask: string = AppContext.supportPage;
  static supportPageTooltip: string = 'Portbase services';

  static isSystemlink: boolean;
  static isNewRoute: boolean;
  static routeData: RouteData;

  static useNewLookAndFeel = (): boolean => {
    const useNewLookAndFeel = localStorage.getItem("useNewLookAndFeel");
    return useNewLookAndFeel === "true" || lodash.isNull(useNewLookAndFeel) || lodash.isUndefined(useNewLookAndFeel);
  }

  static showNewLookAndFeelSwitch = (): boolean => {
    const environmentSettings: { [key: string]: string[] } = newCargoScreenSettings[AppContext.environment] || {};
    const customerSetting = environmentSettings[AppContext.userProfile?.organisation?.shortName];
    return customerSetting == null || customerSetting?.length > 0;
  }

  static ics2ActivatedForOrganisationOrItsCrn = (shortName, crn): boolean => {
    const environmentSettings: { [key: string]: string[] } = newCargoScreenSettings[AppContext.environment] || {};
    const uiSettings = environmentSettings[shortName];
    const tsdCrns = shortName === AppContext.userProfile?.organisation?.shortName
      ? AppContext.userProfile?.organisation?.settings?.tsdPilotCrns || [] : [];
    // No ui settings and no TSD settings in the organisationSettings
    if (uiSettings == null && !tsdCrns.length) {
      return false;
    }
    return uiSettings && uiSettings.length == 0 ? true : (uiSettings || []).concat(tsdCrns).indexOf(crn) > -1;
  }

  static showExemptions(): boolean {
    return this.environment === 'localhost' || this.environment === "kt" || moment().isAfter(moment('2025-07-01'));
  }

  static showFuelExemption(): boolean {
    return this.environment === 'localhost' || this.environment === "kt";
  }

  static navigateWithHistory(router: Router, url: string) {
    router.navigate([url], {queryParams: {eventId: VisitContext.eventId}});
  }

  static isDouaneHaven(portOfCall: PortOfCall) {
    return portOfCall.swDeclarationRequired && !portOfCall.paDeclarationRequired;
  }

  static isAdmin(): boolean {
    return this.strictlyHasRole(Role.VisitPortProcessAdministrator);
  }

  static isCustoms() {
    return AppContext.userProfile.organisation?.shortName === 'DOUAAPELD';
  }

  static isAdminOrCustoms() {
    return AppContext.isCustoms() || AppContext.isAdmin();
  }

  static isWasteCollector() {
    return this.strictlyHasRole(Role.PortVisitWasteCollector);
  }

  static isBunkerOperator() {
    return this.strictlyHasRole(Role.BunkerOperator);
  }

  static isTerminalPlanner(): boolean {
    return this.hasRole(Role.TerminalPlanner);
  }

  static isStevedorePlanner(): boolean {
    return this.hasRole(Role.StevedorePlanner);
  }

  static isStevedorePlannerFor(shortName: string): boolean {
    return this.isStevedorePlanner() && this.userProfile.organisation?.shortName === shortName;
  }

  static isPortAuthority() {
    return this.strictlyHasRole(Role.PortAuthority);
  }

  static isCargoTerminal() {
    return this.strictlyHasRole(Role.ImportCargoDischargeListReceiver)
      || this.strictlyHasRole('sve.dischargelist.receiver.terminaloperator');
  }

  static isTerminalOperatorViewer() {
    return this.strictlyHasRole('sve.ecs.trackandtrace.terminaloperator.view');
  }

  static isCargoDeclarant() {
    return this.strictlyHasRole(Role.DangerousGoodsDeclarantCargoHandlingAgent) || this.isCargoImportViewer();
  }

  static isDangerousGoodsReporter() {
    return this.strictlyHasRole(Role.DangerousGoodsReporterShipOperator);
  }

  static isVisitDeclarant() {
    return this.strictlyHasRole(Role.VisitDeclarant);
  }

  static isCargoImportEditor() {
    return this.strictlyHasRole('eri.em.cargo.declarant.cargohandlingagent.cargo');
  }

  static isCargoImportViewer() {
    return this.isCargoImportEditor() ||
      this.strictlyHasRole(Role.ImportCargoDischargeDifferenceViewer) ||
      this.strictlyHasRole('eri.em.cargo.declarant.cargohandlingagent.cargo.view');
  }

  static hasCargoThroughputData() {
    return AppContext.hasRole(Role.CargoThroughputData);
  }

  static isDischargeListSender() {
    return this.strictlyHasRole(Role.ImportCargoDischargeListSender)
      || this.strictlyHasRole('sve.dischargelist.reporter.cargohandlingagent');
  }

  static isInspectionHandler() {
    return this.strictlyHasRole(Role.IPHandler);
  }

  static isInspectionViewer() {
    return this.strictlyHasRole(Role.IPViewer);
  }

  static isTranshipmentDeclarant() {
    return this.strictlyHasRole(Role.TranshipmentDeclarant);
  }

  static isTransitViewer() {
    return AppContext.isAdminOrCustoms() || this.isTransitEditor() ||
      this.hasRole("eri.ncts.transitdeclaration.declarant.cargohandlingagent.overview.view") ||
      this.hasRole("eri.ncts.transitdeclaration.declarant.forwarder.overview.view") ||
      this.hasRole("eri.ncts.transitdeclaration.declarant.backoffice.overview.view");
  }

  static isTransitEditor() {
    return this.hasRole("eri.ncts.transitdeclaration.declarant.cargohandlingagent.overview.edit") ||
      this.hasRole("eri.ncts.transitdeclaration.declarant.forwarder.overview.edit") ||
      this.hasRole("eri.ncts.transitdeclaration.declarant.backoffice.overview.edit");
  }

  static isTransitGuaranteeEditor() {
    return this.strictlyHasRole('eri.npcs.external.vesselnotification2.transitguaranteedeclarant');
  }

  static isHinterlandTerminal() {
    return this.strictlyHasRole(Role.HinterlandTerminal);
  }

  static isAllowedToAddCommercialRelease() {
    return !this.isProduction() || AppContext.strictlyHasRole(Role.CommercialReleaseDeclarant);
  }

  static isCommercialReleaseDeclarant() {
    return AppContext.hasRole(Role.CommercialReleaseDeclarant);
  }

  static isCommercialReleaseEditor() {
    return AppContext.hasRole(Role.CommercialReleaseEditor);
  }

  static isCommercialReleaseViewer() {
    return AppContext.hasRole(Role.CommercialReleaseViewer);
  }

  static isHarbourDuesReviewer() {
    return AppContext.hasRole(Role.PortDuesOfficer) || AppContext.hasRole(Role.PortDuesAdvisor);
  }

  static isHarbourDuesViewer() {
    return this.isHarbourDuesReviewer() || AppContext.hasRole(Role.PortDuesViewer);
  }

  static isHarbourDuesAdvisor() {
    return AppContext.hasRole(Role.PortDuesAdvisor);
  }

  static hasEmergencyProcedureAccess(): boolean {
    return this.isEmergencyProcedureViewer() || this.isEmergencyProcedureEditor();
  }

  static isEmergencyProcedureViewer(): boolean {
    return AppContext.hasRole(Role.EmergencyProcedureViewer);
  }

  static isEmergencyProcedureEditor(): boolean {
    return AppContext.hasRole(Role.EmergencyProcedureEditor);
  }

  static isProduction() {
    return this.environment === 'pcs';
  }

  static isServiceDesk() {
    return AppContext.userProfile?.userName?.toLowerCase().indexOf('sd@') === 0;
  }

  static isOrganisationAdmin() {
    return this.strictlyHasRole("sve.platform.management.accountmanagement.orgadmin") || this.isServiceDesk();
  }

  static isClearancesActive() {
    return true;
  }

  static isControllingAtaAtdByPortAuthorityActive() {
    return this.isProduction();
  }

  static showFirstLine() {
    return true;
  }

  static showPortCallOptimization() {
    return true;
  }

  static hasRole(role: string): boolean {
    return this.isAdmin() || this.strictlyHasRole(role);
  }

  static strictlyHasRole(role: string): boolean {
    return AppContext.userProfile.roles && AppContext.userProfile.roles.indexOf(role) > -1;
  }

  // This code is a replacement of the code that did a negative check on the role WasteCollector.
  // Probably too many roles are included.
  // So, if you are certain that a role does not have to be included, please remove it.
  static isAllowedToViewVisits() {
    return this.isAdmin() ||
      this.isCustoms() ||
      this.isTerminalPlanner() ||
      this.isStevedorePlanner() ||
      this.isPortAuthority() ||
      this.isCargoTerminal() ||
      this.isTerminalOperatorViewer() ||
      this.isCargoDeclarant() ||
      this.isDangerousGoodsReporter() ||
      this.isVisitDeclarant() ||
      this.isCargoImportEditor() ||
      this.isCargoImportViewer() ||
      this.isDischargeListSender() ||
      this.isInspectionHandler() ||
      this.isInspectionViewer() ||
      this.hasEmergencyProcedureAccess() ||
      this.isHarbourDuesViewer() ||
      AppContext.hasRole(Role.PaxDeclarant) ||
      AppContext.hasRole(Role.ShipSuppliesDeclarant) ||
      AppContext.hasRole(Role.Government) ||
      AppContext.hasRole(Role.CargoDeclarant) ||
      AppContext.hasRole(Role.CargoDataViewer) ||
      AppContext.hasRole(Role.VisitReader) ||
      AppContext.hasRole(Role.ImportCargoDeclarant) ||
      AppContext.hasRole(Role.DangerousGoodsDeclarantCargoHandlingAgent) ||
      AppContext.hasRole(Role.ImportCargoProcessAdministrator) ||
      AppContext.hasRole(Role.VisitPortProcessAdministrator)
  }

  static waitForProcess = (process: Observable<any>): Observable<any> => {
    AppContext.pendingProcesses.push(process);
    const removeProcess = () => AppContext.pendingProcesses.splice(AppContext.pendingProcesses.indexOf(process), 1);
    return process.pipe(tap(removeProcess, removeProcess));
  };

  static registerError(error: any, level: AlertLevel = 'danger'): Alert[] {
    if (typeof error === 'string') {
      const alert = <Alert>{content: error, level: level, type: 'visit'};

      this.alerts = this.alerts.filter(a => a.level === level);
      this.addAlert(alert);

      return [alert];
    } else if (error instanceof HttpErrorResponse) {
      const status = error.status;
      const errorString = error.error?.fullPathError || error.error?.error;

      if (status >= 400 && status < 500 && errorString) {
        const splitErrors = (<string>errorString).split('\n');

        if (splitErrors.length > 1) {
          return splitErrors.flatMap(e => this.registerError(e, level));
        } else {
          return this.registerError(errorString, level);
        }
      } else {
        const errorId = Sentry.captureException(this.extractHttpModuleError(error));

        return this.registerError('An unexpected error occurred (' + status
          + '). Please contact Portbase Customer Service and mention error id: ' + errorId, level);
      }
    } else if (error instanceof Error) {
      return this.registerError(error.message, level);
    } else {
      return this.registerError(JSON.stringify(error), level);
    }
  }

  static hasErrors() {
    return this.alerts.filter(a => a.level === 'danger' && a.type === 'visit').length > 0;
  }

  static registerSuccess(success: string, msShowTime?: number) {
    const alert = <Alert>{content: success, level: 'success', msShowTime: msShowTime == null ? 2000 : msShowTime};
    this.addAlert(alert);
  }

  static addAlert(alert: Alert) {
    if (this.alerts.filter(value => value.content === alert.content).length === 0) {
      this.alerts.push(alert);
    }
  }

  static closeAlerts(alerts: Alert[]) {
    alerts.forEach(alert => {
      if (AppContext.alerts.indexOf(alert) >= 0) {
        AppContext.alerts.splice(AppContext.alerts.indexOf(alert), 1);
      }
    });
  }

  static clearVisitAlerts() {
    this.closeAlerts(this.alerts.filter(value => value.type === "visit"));
  }

  static clearAlerts() {
    this.alerts = [];
  }

  static getUrlPath() {
    return window.location.pathname;
  }

  static dangerousGoodsContextActive() {
    return window.location.pathname.indexOf('dangerousGoods') > 0;
  }

  static setSupportPageForTask() {
    this.supportPageTooltip = 'Portbase services';
    this.supportPageForTask = this.supportPage;

    let urlPath = this.getUrlPath();
    if (urlPath.indexOf('/waste') > -1) {
      this.toSupportPage('/services/melding-afvalstoffen', 'waste collection declaration');
      return;
    }
    let task = this.detectTask(urlPath);
    switch (task) {
      case 'visit':
        this.toSupportPage('/services/melding-schip', 'visit declaration');
        return;
      case 'shipStores':
        this.toSupportPage('/services/melding-scheepsvoorraden', 'ship stores declaration');
        return;
      case 'cargoImport':
        this.toSupportPage('/services/melding-lading-import', 'cargo import declaration');
        return;
      case 'dangerousGoods':
        this.toSupportPage('/services/melding-gevaarlijke-stoffen', 'dangerous goods declaration');
        return;
      case 'passengers':
        this.toSupportPage('/services/melding-bemanning-passagiers', 'crew and passengers declaration');
        return;
    }
  }

  private static toSupportPage(servicePage: string, supportPageTooltip: string) {
    this.supportPageForTask = this.supportPage.concat(servicePage);
    this.supportPageTooltip = supportPageTooltip;
  }

  private static detectTask(urlPath: string): string {
    const match = urlPath.match(/^\/details\/\w+\/(\w+)\/?$/);
    return match ? match[1] : '';
  }

  private static detectEnvironment(): string {
    const newScMatch = window.location.href.match(/https:\/\/portvisit-sc\.kt\.portbase\.com.*/);
    const environmentMatch = window.location.href.match(/https:\/\/portvisit\.(.*)\.portbase\.com.*/);
    const localHostMatch = window.location.href.match(/http:\/\/(localhost)(.*)/);

    if (newScMatch) {
      return "sc";
    } else if (environmentMatch) {
      return environmentMatch[1].toLowerCase();
    } else if (localHostMatch) {
      return localHostMatch[1].toLocaleLowerCase();
    }
    return null;
  }

  private static detectCredentialsEnvironment(): string {
    const newScMatch = window.location.href.match(/https:\/\/portvisit-sc\.kt\.portbase\.com.*/);
    const environmentMatch = window.location.href.match(/https:\/\/portvisit\.(.*)\.portbase\.com.*/);
    const localHostMatch = window.location.href.match(/http:\/\/(localhost)(.*)/);

    if (newScMatch) {
      return "kt";
    } else if (environmentMatch) {
      const environment = environmentMatch[1].toLowerCase();

      if (environment === "dev") {
        return "kt";
      } else {
        return environment;
      }
    } else if (localHostMatch) {
      return localHostMatch[1].toLocaleLowerCase();
    }
    return null;
  }

  static getRefreshUrl(): string {
    return `https://supporting.${this.credentialsEnvironment}.portbase.com/token/refresh`;
  }

  static getMaritimeFormalitiesLink(crn: string): string {
    const environment = ["kt", "accept", "pcs"].includes(this.environment) ? this.environment : "kt";

    return `https://authorityhub.${environment}.portbase.com/formalities?search=${crn}`;
  }

  private static detectIamEnvironment(): string {
    const environment = this.detectEnvironment();

    if (environment === "pcs") {
      return "iamconnected";
    } else if (["accept", "dev", "kt", "sc"].includes(environment)) {
      return "preview-iamconnected";
    } else {
      return null;
    }
  }

  private static detectUrlPrefix(): string {
    const newScMatch = window.location.href.match(/https:\/\/portvisit-sc\.kt\.portbase\.com.*/);
    const environmentMatch = window.location.href.match(/https:\/\/portvisit\.(.*)\.portbase\.com.*/);
    const localHostMatch = window.location.href.match(/http:\/\/(localhost)(.*)/);

    if (newScMatch) {
      return "portbase-sc.kt";
    } else if (environmentMatch) {
      return "portvisit." + environmentMatch[1].toLowerCase();
    } else if (localHostMatch) {
      return localHostMatch[1].toLocaleLowerCase();
    }
    return null;
  }

  static getDocument() {
    return document;
  }

  static isVlissingenOrTerneuzenVisit(portUnCode: string) {
    return ['NLVLI', 'NLTNZ'].includes(portUnCode);
  }

  static adminAllowedToUpdateAtaAtd(portUnloCode: string): boolean {
    return this.isAdmin() && !(this.ataAtdIsControlledByPortAuthorityOfPort(portUnloCode));
  }

  static ataAtdIsControlledByPortAuthorityOfPort(portUnloCode: string): boolean {
    return this.isControllingAtaAtdByPortAuthorityActive() && this.portsThatControlsAtaAtd().includes(portUnloCode);
  }

  static portsThatControlsAtaAtd(): string[] {
    return ['NLRTM', 'NLAMS'];
  }

  static cargoDeclarantUsesNewCargoScreen(cargoDeclarantShortName: string): boolean {
    const environmentSettings: { [key: string]: string[] } = newCargoScreenSettings[AppContext.environment] || {};
    const crns = environmentSettings[cargoDeclarantShortName];
    const isOwnOrgWithTsdDateSet = cargoDeclarantShortName === AppContext.userProfile?.organisation?.shortName
      && AppContext.userProfile?.organisation?.settings?.tsdTimeFirstIcs2Visit != null;
    return (crns != null && crns.length === 0) || isOwnOrgWithTsdDateSet;
  }

  static cargoDeclarantUsesNewCargoScreenForCrn(cargoDeclarantShortName: string, crn: string): boolean {
    const environmentSettings: { [key: string]: string[] } = newCargoScreenSettings[AppContext.environment] || {};
    const crns = environmentSettings[cargoDeclarantShortName];
    const tsdCrns = cargoDeclarantShortName === AppContext.userProfile?.organisation?.shortName
      ? AppContext.userProfile?.organisation?.settings?.tsdPilotCrns || [] : [];
    return (crns != null && (crns.includes(crn) || crns.length === 0)) || (tsdCrns || []).includes(crn);
  }

  static commercialReleaseDeclarantUsesNewCommercialReleaseScreen(organisationShortName: string): boolean {
    // new-commercial-release-screen-settings.json's on-disk shape is a flat array of
    // enabled organisation short names per environment (e.g. {"kt": ["CROSSOCEANRTM"]}),
    // not cargoDeclarantUsesNewCargoScreen's nested {env: {org: crns[]}} shape - so this
    // is a membership check, not a dict lookup.
    const environmentSettings: string[] = newCommercialReleaseScreenSettings[AppContext.environment] || [];
    return environmentSettings.includes(organisationShortName);
  }

  // Shared by the nav tab getter (AppComponent.commercialReleaseNewScreenEnabled) and
  // commercialReleaseRedirectGuard — keep both call sites on this single formula so they
  // can never disagree with each other.
  static commercialReleaseNewScreenEnabled(): boolean {
    const enabledForEnvironment = AppContext.isAdmin()
      || AppContext.commercialReleaseDeclarantUsesNewCommercialReleaseScreen(AppContext.userProfile.organisation.shortName)
      || AppContext.environment === "kt";
    return enabledForEnvironment && AppContext.useNewLookAndFeel();
  }

  static extractHttpModuleError(error: HttpErrorResponse): string | Error {
    // The `error` property of http exception can be either an `Error` object, which we can use directly...
    if (this.isErrorOrErrorLikeObject(error.error)) {
      return error.error;
    }

    // ... or an`ErrorEvent`, which can provide us with the message but no stack...
    if (error.error instanceof ErrorEvent && error.error.message) {
      return error.error.message;
    }

    // ...or the request body itself, which we can use as a message instead.
    if (typeof error.error === 'string') {
      return `Server returned code ${error.status} with body "${error.error}"`;
    }

    // If we don't have any detailed information, fallback to the request message itself.
    return error.message;
  }

  static isErrorOrErrorLikeObject(value: unknown): value is Error {

    if (value instanceof Error) {
      return true;
    }

    if (value === null || typeof value !== 'object') {
      return false;
    }

    const candidate = value as ErrorCandidate;

    return (
      isString(candidate.name) &&
      isString(candidate.name) &&
      isString(candidate.message) &&
      (undefined === candidate.stack || isString(candidate.stack))
    );
  }

  static useOpenSearch() {
    const useOpenSearch = localStorage.getItem("useOpenSearch");
    return useOpenSearch === "true";
  }
}

type ErrorCandidate = {
  name?: unknown;
  message?: unknown;
  stack?: unknown;
};
